import React, { useState } from 'react';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { useChecklist } from './useChecklist';
import { AddEditDocumentModal } from './AddEditDocumentModal';
import { EstatePlanningDocumentDTO } from '../../../../../types/goalsDTO';
import { EstatePlanningDocumentType, EstatePlanningTab } from '../../EstatePlanning.types';
import { Explanations } from '../../../../components/Explanations/Explanations';
import { useDeleteEstatePlanningDocumentMutation } from '../../api/useDeleteEstatePlanningDocumentMutation';
import { Modal } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Tasks } from '../../../../components/Tasks/Tasks';
import { useDocumentTitle } from '../../../../../../_hooks/useDocumentTitle';
import { ChecklistTable } from './components/ChecklistTable/ChecklistTable';

const NEW_DOCUMENT = {
  Id: '',
  Checked: false,
  Description: null,
  DocumentType: EstatePlanningDocumentType.Will,
  FileUrl: null,
  LastDocumentUpdate: null,
  Notes: null,
};

interface ChecklistProps {
  isPrint?: boolean;
}

export const Checklist = ({ isPrint = false }: ChecklistProps) => {
  useDocumentTitle('Goals - Estate Planning Checklist');

  const [selectedDocument, setSelectedDocument] = useState<EstatePlanningDocumentDTO | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<EstatePlanningDocumentDTO | null>(null);

  const {
    mutateAsync: deleteDocument,
    isLoading: isDeletingDocument,
  } = useDeleteEstatePlanningDocumentMutation();

  const data = useChecklist({ isPrint });

  if (!data) {
    return null;
  }

  const { clientId, documentGroups, documentTypes, goal } = data;

  return (
    <Box mb={isPrint ? '0' : '32'}>
      {!isPrint && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Explanations selectedTab={EstatePlanningTab.Checklist} my="24" />

          <Button leftIcon="fas fa-plus" onClick={() => setSelectedDocument(NEW_DOCUMENT)}>
            Add Other Document
          </Button>
        </Box>
      )}

      {documentGroups.map(({ id, name, documents }) => (
        <ChecklistTable
          key={id}
          name={name}
          documents={documents}
          onSelectedDocument={setSelectedDocument}
          onSetDocumentToDelete={setDocumentToDelete}
        />
      ))}

      {selectedDocument !== null && (
        <AddEditDocumentModal
          document={selectedDocument}
          isOpen
          onRequestClose={() => setSelectedDocument(null)}
          documentTypes={documentTypes}
        />
      )}

      <Tasks goalId={goal.GoalId} />

      <Modal
        isOpen={documentToDelete !== null}
        onRequestClose={() => setDocumentToDelete(null)}
        aria={{ labelledby: 'modal-header' }}
      >
        <ModalHeader as="h2" id="modal-header">
          Delete Document
        </ModalHeader>
        <ModalCloseButton onClick={() => setDocumentToDelete(null)} />

        <ModalBody>
          <Text>Are you sure you want to delete this document?</Text>
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={() => setDocumentToDelete(null)}>
            Cancel
          </Button>

          <Button
            type="submit"
            isLoading={isDeletingDocument}
            onClick={async () => {
              if (documentToDelete !== null) {
                await deleteDocument({ id: documentToDelete.Id, clientId });
                setDocumentToDelete(null);
              }
            }}
            ml="8"
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </Box>
  );
};
